import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Progress } from 'semantic-ui-react'
import Layout from '~/components/Layout'
import { useNavigate, useParams } from 'react-router-dom'
import { RouteTypes } from '~/routes/constants'
import { El } from '~/components/Layout/style'
import notify from '~/components/Notify'
import { LoginPopupsService, PopupOptionsType } from '~/api/services/loginPopups'
import { FormFields } from '~/components/FormFields'
import { FormProvider, useForm } from 'react-hook-form'
import axios from 'axios'
import ENV from '~/environment'
import dayjs from 'dayjs'
import { ProgressWrapper } from '../../../CloudStorage/style'
import { formatNumberWithoutFractions, handleErrors } from '~/utils/helpers'
import Dialog from '~/components/Dialog'
import { Nullable } from '~/types'
import InfoPopup from './InfoPopup'
import { useAuth } from '~/routes/components/AuthProvider'
import DataPopup from './DataPopup'
import { buildInfoFormData, buildDataPayload } from './util'

const INFO_KEY = 1
const DATA_KEY = 2

const CreateEditLoginPopup: React.FunctionComponent = () => {
  const { systemData: { languages } } = useAuth()
  const [optionsData, setOptionsData] = useState<Nullable<PopupOptionsType>>(null)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams()

  const [isUploading, setIsUploading] = useState(false)
  const [isBusy, setIsBusy] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [abortController, setAbortController] = useState<AbortController | null>(null)

  const methods = useForm({
    mode: 'onSubmit'
  })

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    reset
  } = methods

  const watchPopupType = watch('type')

  useEffect(() => {
    const controller = new AbortController()
    fetchData(controller.signal)

    return () => {
      controller.abort()
    }
  }, [t])

  async function getFileUrls(id, languages) {
    for (const language of languages) {
      try {
        const response = await LoginPopupsService.getFileUrl(id, { fileName: language.content })
        language.file = response.data
      } catch (error) {
        // do nothing
      }
    }
    return languages
  }

  async function fetchData(signal: AbortSignal) {
    setIsBusy(true)
    try {
      const responseOptions = await LoginPopupsService.getOptionsData(signal)
      setOptionsData(responseOptions.data)
      if (id) {
        const response = await LoginPopupsService.getLoginPopup(id, signal)
        response.data.from = response.data.from ? dayjs(response.data.from).format('DD.MM.YYYY HH:mm') : ''
        response.data.to = response.data.to ? dayjs(response.data.to).format('DD.MM.YYYY HH:mm') : ''
        const languagesEdit = response.data.languages
        languages.map((language) => {
          const indexLang = languagesEdit.findIndex(languageEd => languageEd.id === language.id)
          if (indexLang < 0) {
            languagesEdit.push({
              id: language.id,
              headerText: '',
              footerText: '',
              file: '',
              title: '',
              order: language.order,
              label: language.label
            })
          } else {
            languagesEdit[indexLang].order = language.order
          }
        })
        languagesEdit.sort((a, b) => a.order - b.order)
        languagesEdit.forEach(lang => delete lang.order)
        if (response.data.type === INFO_KEY) {
          response.data.languages = await getFileUrls(id, languagesEdit)
        } else {
          response.data.languages = languagesEdit
          const role = responseOptions.data?.roles.find(roleItem => roleItem.id === response?.data?.roles[0])
          response.data.fields = response.data?.fields?.map(field => {
            const fieldOption = responseOptions?.data?.fields[role.slug]?.find(fieldOption => fieldOption.slug === field.slug)
            return {
              ...fieldOption,
              ...field
            }
          })
        }
        reset({
          ...response.data,
          roles: response.data?.roles[0]
        })
      } else {
        const languagesNew = []
        languages.map((language) => {
          languagesNew.push({
            id: language.id,
            headerText: '',
            footerText: '',
            file: '',
            title: '',
            label: language.label
          })
        })
        setValue('languages', languagesNew)
      }
    } catch (error) {
      if (error.response?.data) {
        notify(error.response.data.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
    setIsBusy(false)
  }

  async function onSubmit(data) {
    const url = id ? `/settings/pop-ups/${id}/update` : '/settings/pop-ups/store'
    const controller = new AbortController()
    setAbortController(controller)
    let payload: FormData | Record<string, any>
    let hasFiles = false

    if (data.type === INFO_KEY) {
      const result = buildInfoFormData(data)
      payload = result.formData
      hasFiles = result.hasFiles
    } else {
      payload = buildDataPayload(data)
    }

    setIsUploading(hasFiles)
    setIsBusy(!hasFiles)
    try {
      await axios.post(
        `${ENV.API_URL}${url}`,
        payload,
        {
          headers: { 'Authorization': localStorage.getItem('token') },
          onUploadProgress: (progressEvent) => {
            const { loaded, total } = progressEvent
            setUploadProgress((loaded / total) * 100)
          },
          signal: controller.signal
        })
      const successMessage = id ? t('Successfully updated login popup') : t('Successfully created login popup')
      notify(successMessage, 'success')
      setIsUploading(false)
      setIsBusy(false)
      navigate(RouteTypes.SETTINGS_LOGIN_POPUP)
    } catch (error) {
      if (axios.isCancel(error)) {
        notify(t('Upload canceled'), 'error')
      } else {
        handleErrors(error)
      }
    }
    setIsBusy(false)
    setIsUploading(false)
  }

  function renderTypeSelect() {
    return <FormFields.Select
      label={t('Popup type')}
      control={control}
      name={'type'}
      placeholder={t('Select popup type')}
      options={optionsData?.types?.map(type => ({ value: type.key, text: type.label }))}
      rules={{
        required: t('Please select popup type')
      }}
      onChange={(_, data) => {
        const languagesEdit = []
        languages.map((language) => {
          const indexLang = languagesEdit.findIndex(languageEd => languageEd.id === language.id)
          if (indexLang < 0) {
            languagesEdit.push({
              id: language.id,
              headerText: '',
              footerText: '',
              file: '',
              title: '',
              order: language.order,
              label: language.label
            })
          } else {
            languagesEdit[indexLang].order = language.order
          }
        })
        languagesEdit.sort((a, b) => a.order - b.order)
        languagesEdit.forEach(lang => delete lang.order)
        reset({
          type: data.value,
          languages: languagesEdit
        })
      }}
      disabled={Boolean(id)}
      selectOnBlur={false}
      selection
    />
  }

  function renderSelectedPopup() {
    switch (watchPopupType) {
      case INFO_KEY:
        return <InfoPopup optionsData={optionsData} />
      case DATA_KEY:
        return <DataPopup optionsData={optionsData} />
      default:
        return null
    }
  }

  function renderActions() {
    if (!watchPopupType) {
      return null
    }
    return <El.PageHeaderActionButton
      disabled={isUploading}
    >{id ? t('Update') : t('Save')}</El.PageHeaderActionButton>
  }

  function renderProgressBar() {
    const uploadPercent = `${formatNumberWithoutFractions(uploadProgress)}%`
    return <ProgressWrapper>
      <Progress percent={uploadProgress} indicating className='custom-progress' />
      <p className='text-below-progress'>{t('Uploaded: {{uploadPercent}}', { uploadPercent })}</p>
    </ProgressWrapper>
  }

  function renderUpload() {
    return <Dialog
      open={Boolean(isUploading)}
      textHeader={t('Upload')}
      htmlContent={false}
      textContent={<div style={{ position: 'relative' }}>
        {renderProgressBar()}
      </div>}
      contentAutoSize
      textClose={t('Cancel')}
      handleClose={() => {
        if (abortController) {
          abortController.abort()
        }
        setIsUploading(false)
      }}
    />
  }

  return <Layout
    title={id ? t('Update login popup') : t('Create login popup')}
    isBusy={isBusy}
    segmented
    close={RouteTypes.SETTINGS_LOGIN_POPUP}
    actions={renderActions()}
    onSubmit={handleSubmit(onSubmit)}
  >
    {renderTypeSelect()}
    <FormProvider {...methods}>
      {watchPopupType && renderSelectedPopup()}
    </FormProvider>
    {isUploading && uploadProgress > 0 && renderUpload()}
  </Layout>
}

export default CreateEditLoginPopup
